import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Trash2, Plus, Pencil, Save, X, Search, MessageCircle,
  ShieldAlert, ListFilter, ScrollText,
} from "lucide-react";
import {
  chatbotMeta, chatbotFaqList, chatbotFaqCreate, chatbotFaqUpdate,
  chatbotFaqDelete, chatbotLogs,
} from "../adminApi";

const EMPTY = {
  question: "", answer: "", keywords: "", category: "General", actions: [], active: true,
};

export default function AdminChatbot() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], actions: {}, forbidden: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // faq object or "new"
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [tab, setTab] = useState("faqs"); // "faqs" | "logs"
  const [logs, setLogs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, list] = await Promise.all([chatbotMeta(), chatbotFaqList()]);
      setMeta(m);
      setItems(list);
    } catch {
      toast.error("Failed to load chatbot data.");
    } finally { setLoading(false); }
  };

  const loadLogs = async () => {
    try { setLogs(await chatbotLogs()); } catch { toast.error("Failed to load logs."); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "logs") loadLogs(); }, [tab]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== "All" && it.category !== cat) return false;
      if (!qq) return true;
      const hay = [it.question, it.answer, (it.keywords || []).join(" "), it.category].join(" ").toLowerCase();
      return hay.includes(qq);
    });
  }, [items, q, cat]);

  const openNew = () => { setEditing("new"); setForm(EMPTY); setErrors({}); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      question: f.question || "", answer: f.answer || "",
      keywords: (f.keywords || []).join(", "),
      category: f.category || "General",
      actions: f.actions || [],
      active: f.active !== false,
    });
    setErrors({});
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setErrors({}); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Question and answer are required.");
      return;
    }
    setSaving(true);
    setErrors({});
    const body = {
      question: form.question.trim(),
      answer: form.answer.trim(),
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      category: form.category,
      actions: form.actions,
      active: form.active,
    };
    try {
      if (editing === "new") {
        await chatbotFaqCreate(body);
        toast.success("FAQ created.");
      } else {
        await chatbotFaqUpdate(editing.id, body);
        toast.success("FAQ updated.");
      }
      cancel();
      load();
    } catch (err) {
      if (err?.errors) setErrors(err.errors);
      toast.error(err?.message || "Save failed.");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await chatbotFaqDelete(id); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const toggleAction = (key) => {
    setForm((s) => ({
      ...s,
      actions: s.actions.includes(key) ? s.actions.filter((a) => a !== key) : [...s.actions, key],
    }));
  };

  return (
    <div data-testid="admin-chatbot" className="max-w-6xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <MessageCircle className="h-6 w-6 text-orange" /> Chatbot Q&A
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage the approved knowledge base for the ASAI Information Assistant.
            Forbidden phrases (e.g., &ldquo;guaranteed placement&rdquo;) are blocked automatically.
          </p>
        </div>
        {tab === "faqs" && !editing && (
          <button onClick={openNew} data-testid="chatbot-faq-new"
            className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-medium text-white hover:bg-orange/90">
            <Plus className="h-4 w-4" /> New FAQ
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit">
        <TabBtn active={tab === "faqs"} onClick={() => setTab("faqs")} testId="chatbot-tab-faqs"><ListFilter className="h-3.5 w-3.5" /> FAQs</TabBtn>
        <TabBtn active={tab === "logs"} onClick={() => setTab("logs")} testId="chatbot-tab-logs"><ScrollText className="h-3.5 w-3.5" /> Recent Questions</TabBtn>
      </div>

      {/* Forbidden phrases banner */}
      {tab === "faqs" && meta.forbidden?.length > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="mb-1 font-semibold">Guardrail active</div>
            The following phrases are blocked in questions and answers:{" "}
            <span className="font-mono">{meta.forbidden.join(" · ")}</span>. ASAI provides
            placement <em>assistance</em> — never guarantees.
          </div>
        </div>
      )}

      {/* Edit form (in-place) */}
      {tab === "faqs" && editing && (
        <FaqForm
          form={form} setForm={setForm}
          meta={meta} errors={errors}
          onSubmit={submit} onCancel={cancel}
          onToggleAction={toggleAction} saving={saving}
          isNew={editing === "new"}
        />
      )}

      {/* FAQ list */}
      {tab === "faqs" && !editing && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search FAQs…"
                data-testid="chatbot-search"
                className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange" />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="chatbot-cat-filter"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange">
              <option value="All">All Categories</option>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-400">
              No FAQs match your filter.
            </div>
          ) : (
            <div className="space-y-3" data-testid="chatbot-faq-list">
              {filtered.map((f) => (
                <div key={f.id} data-testid={`chatbot-faq-row-${f.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-orange/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange">{f.category}</span>
                    {f.active === false && <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">Hidden</span>}
                    <span className="ml-auto flex items-center gap-1">
                      <button onClick={() => openEdit(f)} data-testid={`chatbot-faq-edit-${f.id}`}
                        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-orange">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => del(f.id)} data-testid={`chatbot-faq-delete-${f.id}`}
                        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                  <div className="font-semibold text-slate-900">{f.question}</div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{f.answer}</div>
                  {(f.keywords?.length > 0 || f.actions?.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
                      {f.keywords?.map((k) => (
                        <span key={k} className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-slate-600">{k}</span>
                      ))}
                      {f.actions?.map((a) => (
                        <span key={a} className="rounded-full bg-royal/10 px-2 py-0.5 font-medium text-royal">{a}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Logs tab */}
      {tab === "logs" && (
        <div>
          <p className="mb-3 text-xs text-slate-500">
            Recent user questions. Use these to spot missing FAQs (source = <code>fallback</code>).
          </p>
          {logs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-400">
              No conversations yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="chatbot-logs">
              {logs.map((l) => (
                <div key={l.id} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                  <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${l.source === "fallback" ? "bg-amber-400" : "bg-emerald-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800">{l.question}</div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">→ {l.reply}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                      {l.source} · {(l.createdAt || "").slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-orange text-white" : "text-slate-600 hover:bg-slate-100"}`}>
    {children}
  </button>
);

const FaqForm = ({ form, setForm, meta, errors, onSubmit, onCancel, onToggleAction, saving, isNew }) => {
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <form onSubmit={onSubmit} data-testid="chatbot-faq-form"
      className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{isNew ? "New FAQ" : "Edit FAQ"}</h2>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700" data-testid="chatbot-faq-form-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Question</label>
          <input value={form.question} onChange={(e) => set("question", e.target.value)}
            data-testid="chatbot-faq-question"
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-orange ${errors.question ? "border-red-400" : "border-slate-300"}`} />
          {errors.question && <p className="mt-1 text-xs text-red-500">{errors.question}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Approved Answer</label>
          <textarea value={form.answer} rows={5} onChange={(e) => set("answer", e.target.value)}
            data-testid="chatbot-faq-answer"
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-orange ${errors.answer ? "border-red-400" : "border-slate-300"}`} />
          {errors.answer && <p className="mt-1 text-xs text-red-500">{errors.answer}</p>}
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Keywords (comma-separated)</label>
          <input value={form.keywords} onChange={(e) => set("keywords", e.target.value)}
            placeholder="e.g. fee, cost, price"
            data-testid="chatbot-faq-keywords"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)}
            data-testid="chatbot-faq-category"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange">
            {meta.categories?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="mb-2 block text-xs font-semibold uppercase text-slate-500">Suggested Actions (buttons under the bot reply)</label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(meta.actions || {}).map(([k, meta]) => {
              const active = form.actions.includes(k);
              return (
                <button type="button" key={k} onClick={() => onToggleAction(k)}
                  data-testid={`chatbot-faq-action-${k}`}
                  className={`rounded-full border px-3 py-1 text-xs ${active ? "border-orange bg-orange text-white" : "border-slate-300 text-slate-600 hover:border-orange"}`}>
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
        <label className="mt-1 inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
            data-testid="chatbot-faq-active" className="h-4 w-4 rounded" />
          Active (visible to users)
        </label>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="chatbot-faq-form-cancel"
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} data-testid="chatbot-faq-form-save"
          className="inline-flex items-center gap-2 rounded-lg bg-orange px-5 py-2 text-sm font-medium text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save FAQ"}
        </button>
      </div>
    </form>
  );
};
